import React, { useState, useEffect, lazy, Suspense, useMemo } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useParams
} from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { Box, useMediaQuery } from "@mui/material";
import { useAuthStore } from "./store/authStore";
import ProtectedRoute from "./components/guards/ProtectedRoute";
import Navbar from "./components/layout/Navbar";
import Sidebar from "./components/layout/Sidebar";
import Footer from "./components/layout/Footer";
import LoadingSpinner from "./components/ui/LoadingSpinner";
import UpdateBanner from "./components/ui/UpdateBanner";
import UpgradeGateListener from "./components/common/UpgradeGateListener";
import AppLoader from "./components/loading/AppLoader";
import PageLoader from "./components/loading/PageLoader";
import { ROUTES, STORAGE_KEYS } from "./utils/constants";
import { TenantProvider, useTenant } from "./context/TenantContext";
import { EmojiCatalogProvider } from "./context/EmojiCatalogContext";
import { readableOn } from "./utils/brandColor";

const Login = lazy(() => import("./pages/auth/Login"));
const Register = lazy(() => import("./pages/auth/Register"));
const ForgotPassword = lazy(() => import("./pages/auth/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/auth/ResetPassword"));
const StudentDashboard = lazy(() => import("./pages/dashboard/StudentDashboard"));
const TeacherDashboard = lazy(() => import("./pages/dashboard/TeacherDashboard"));
const UniversityAdminDashboard = lazy(() => import("./pages/dashboard/UniversityAdminDashboard"));
const SuperAdminDashboard = lazy(() => import("./pages/dashboard/SuperAdminDashboard"));
const HeadOfFacultyDashboard = lazy(() => import("./pages/dashboard/HeadOfFacultyDashboard"));
const TiersPage = lazy(() => import("./pages/faculty/TiersPage"));
const TranscriptPage = lazy(() => import("./pages/academics/TranscriptPage"));
const AcademicPeriodsPage = lazy(() => import("./pages/academics/AcademicPeriodsPage"));
const SpotlightPage = lazy(() => import("./pages/academics/SpotlightPage"));
const CourseDetail = lazy(() => import("./pages/courses/CourseDetail"));
const MyCourses = lazy(() => import("./pages/courses/MyCourses"));
const TeachingCourses = lazy(() => import("./pages/courses/TeachingCourses"));
const CourseCatalog = lazy(() => import("./pages/enrollment/CourseCatalog"));
const CreateCourse = lazy(() => import("./pages/courses/CreateCourse"));
const ContentUpload = lazy(() => import("./pages/courses/ContentUpload"));
const LecturePlayer = lazy(() => import("./pages/courses/LecturePlayer"));
const Bookmarks = lazy(() => import("./pages/bookmarks/Bookmarks"));
const QuizList = lazy(() => import("./pages/quizzes/QuizList"));
const QuizScreen = lazy(() => import("./pages/quizzes/QuizScreen"));
const QuizBuilder = lazy(() => import("./pages/quizzes/QuizBuilder"));
const QuizSubmissions = lazy(() => import("./pages/quizzes/QuizSubmissions"));
const SubmissionGrader = lazy(() => import("./pages/submissions/SubmissionGrader"));
const AssignmentList = lazy(() => import("./pages/assignments/AssignmentList"));
const AssignmentCreate = lazy(() => import("./pages/assignments/AssignmentCreate"));
const AssignmentSubmissionPage = lazy(() => import("./pages/assignments/AssignmentSubmissionPage"));
const AssignmentSubmissions = lazy(() => import("./pages/assignments/AssignmentSubmissions"));
const AssignmentGrader = lazy(() => import("./pages/assignments/AssignmentGrader"));
const DiscussionBoard = lazy(() => import("./pages/discussions/DiscussionBoard"));
const DiscussionThread = lazy(() => import("./pages/discussions/DiscussionThread"));
const NotificationsPage = lazy(() => import("./pages/notifications/Notifications"));
const Profile = lazy(() => import("./pages/profile/Profile"));
const MyCertificates = lazy(() => import("./pages/certificates/MyCertificates"));
const CourseAuthoringStudio = lazy(() => import("./pages/instructor/CourseAuthoringStudio"));
const CourseBroadcasts = lazy(() => import("./pages/live/CourseBroadcasts"));
// The studio is mounted by LiveSessionHost (outside <Routes>) so a teacher can
// navigate the app mid-lecture without dropping the broadcast; the route below
// only supplies the portal target.
const LiveSessionHost = lazy(() => import("./pages/live/LiveSessionHost"));
const StudioRoute = lazy(() =>
  import("./pages/live/LiveSessionHost").then((m) => ({ default: m.StudioRoute }))
);
const LiveViewer = lazy(() => import("./pages/live/LiveViewer"));
const CertificateVerify = lazy(() => import("./pages/certificates/CertificateVerify"));
const CreateUniversity = lazy(() => import("./pages/universities/CreateUniversity"));
const ManageUniversity = lazy(() => import("./pages/universities/ManageUniversity"));
// Auth pages
const VerifyEmailPage = lazy(() => import("./pages/auth/VerifyEmailPage"));
const AwaitingApproval = lazy(() => import("./pages/auth/AwaitingApproval"));
// Error pages
const NotFound = lazy(() => import("./pages/error/NotFound"));
const Unauthorized = lazy(() => import("./pages/error/Unauthorized"));
// Billing pages
const SelectPlanPage = lazy(() => import("./pages/billing/SelectPlanPage"));
const BillingCheckoutPage = lazy(() => import("./pages/billing/BillingCheckoutPage"));
const BillingDashboardPage = lazy(() => import("./pages/billing/BillingDashboardPage"));
// Super Admin pages
const CoursesPage = lazy(() => import("./pages/admin/CoursesPage"));
const UsersPage = lazy(() => import("./pages/admin/UsersPage"));
const DepartmentsPage = lazy(() => import("./pages/admin/DepartmentsPage"));
const AnalyticsPage = lazy(() => import("./pages/admin/AnalyticsPage"));
const UniversitiesPage = lazy(() => import("./pages/admin/UniversitiesPage"));
const SystemSettingsPage = lazy(() => import("./pages/admin/SystemSettingsPage"));
const ErrorMonitoringPage = lazy(() => import("./pages/admin/ErrorMonitoringPage"));
const SuperAdminGuidePage = lazy(() => import("./pages/admin/SuperAdminGuidePage"));
const AdminsPage = lazy(() => import("./pages/admin/AdminsPage"));
const EmojiStudioPage = lazy(() => import("./pages/emoji/EmojiStudioPage"));
const CalendarPage = lazy(() => import("./pages/admin/CalendarPage"));
const GradesPage = lazy(() => import("./pages/grades/GradesPage"));
const Gradebook = lazy(() => import("./pages/grades/Gradebook"));
const CertificateDesigner = lazy(() => import("./pages/certificates/CertificateDesigner"));
const RankingsPage = lazy(() => import("./pages/rankings/RankingsPage"));
const FacultyAdvising = lazy(() => import("./pages/faculty/FacultyAdvising"));
const SettingsPage = lazy(() => import("./pages/settings/settings"));
const HelpPage = lazy(() => import("./pages/help/HelpPage"));
const LearningHubPage = lazy(() => import("./pages/learning/LearningHubPage"));
const ResearchHub = lazy(() => import("./pages/research/ResearchHub"));
const EnrollmentPage  = lazy(() => import("./pages/enrollment/EnrollmentPage"));
const TakeAttendance  = lazy(() => import("./pages/attendance/TakeAttendance"));
const AttendanceReports = lazy(() => import("./pages/attendance/AttendanceReports"));
const AdviseesPage = lazy(() => import("./pages/advising/AdviseesPage"));
// Public company / legal pages (footer "Company" links)
const AboutUs = lazy(() => import("./pages/company/AboutUs"));
const Careers = lazy(() => import("./pages/company/Careers"));
const Press = lazy(() => import("./pages/company/Press"));
const ContactPage = lazy(() => import("./pages/company/Contact"));
const PrivacyPolicy = lazy(() => import("./pages/company/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/company/TermsOfService"));
// Public marketing homepage (root "/")
const LandingPage = lazy(() => import("./pages/marketing/LandingPage"));
// Public product / marketing pages (footer "Product" links)
const Features = lazy(() => import("./pages/product/Features"));
const Pricing = lazy(() => import("./pages/product/Pricing"));
const ForUniversities = lazy(() => import("./pages/product/ForUniversities"));
const ForTeachers = lazy(() => import("./pages/product/ForTeachers"));
const ForStudents = lazy(() => import("./pages/product/ForStudents"));
// Public resources pages (footer "Resources" links)
const Documentation = lazy(() => import("./pages/resources/Documentation"));
const Tutorials = lazy(() => import("./pages/resources/Tutorials"));
const Blog = lazy(() => import("./pages/resources/Blog"));
const BlogPost = lazy(() => import("./pages/resources/BlogPost"));
const Support = lazy(() => import("./pages/resources/Support"));
const Api = lazy(() => import("./pages/resources/Api"));
const AdaptiveLearningDashboard = lazy(() => import("./pages/dashboard/AdaptiveLearningDashboard"));
const LearningCoachDashboard = lazy(() => import("./pages/dashboard/LearningCoachDashboard"));
const LearningAnalyticsDashboard = lazy(() => import("./pages/dashboard/LearningAnalyticsDashboard"));

const CoursePageWrapper = ({ Component }) => {
  const { courseId } = useParams();
  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Component courseId={courseId} />
    </Box>
  );
};

const AppLayout = ({ children }) => {
  const user = useAuthStore((s) => s.user);
  const location = useLocation();
  // Below `md` the sidebar is a temporary overlay; at/above it's a docked rail.
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    // On phones/tablets the sidebar must start closed so it never covers content.
    if (typeof window !== "undefined" && window.matchMedia("(max-width: 899.95px)").matches) {
      return false;
    }
    const saved = localStorage.getItem(STORAGE_KEYS.SIDEBAR_OPEN);
    return saved !== null ? JSON.parse(saved) : true;
  });

  // Persist only the desktop preference — the mobile overlay state is transient.
  useEffect(() => {
    if (!isMobile) {
      localStorage.setItem(STORAGE_KEYS.SIDEBAR_OPEN, JSON.stringify(sidebarOpen));
    }
  }, [sidebarOpen, isMobile]);

  // Auto-close the overlay after navigating (or when shrinking to mobile) so the
  // central content is never hidden behind the drawer on small screens.
  useEffect(() => {
    if (isMobile) setSidebarOpen(false);
  }, [isMobile, location.pathname]);

  // Public marketing/company/legal/resources pages render with their own chrome.
  const COMPANY_PATHS = [
    "/about", "/careers", "/press", "/contact", "/privacy", "/terms",
    "/features", "/pricing", "/for-universities", "/for-teachers", "/for-students",
    "/docs", "/tutorials", "/blog", "/support", "/api",
  ];

  const isAuthPage = (
    [ROUTES.LOGIN, ROUTES.REGISTER, ROUTES.FORGOT_PASSWORD, ROUTES.RESET_PASSWORD].includes(location.pathname) ||
    location.pathname.startsWith("/verify-email") ||
    location.pathname === "/select-plan" ||
    location.pathname.startsWith("/billing/checkout") ||
    location.pathname.startsWith("/blog/") ||
    COMPANY_PATHS.includes(location.pathname)
  );

  if (isAuthPage || !user) {
    return <Box sx={{ minHeight: "100vh" }}>{children}</Box>;
  }

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <Sidebar
        open={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
      />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          // `minWidth: 0` lets a flex child shrink below its content width so
          // wide tables/cards scroll internally instead of pushing the layout
          // (and the sidebar) out of alignment. The docked sidebar already
          // reserves its own width, so NO left margin is needed here.
          minWidth: 0,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          /**
           * ⚠️ `clip`, not `hidden`, and the difference is not cosmetic.
           *
           * Setting ONE axis to a non-`visible` overflow forces the other axis
           * to compute as `auto` (CSS spec), so `overflowX: "hidden"` quietly
           * made this element a SCROLL CONTAINER on the Y axis. A scroll
           * container is what `position: sticky` measures itself against — and
           * this one never scrolls, because the document does. The result: every
           * `position: sticky` under the app shell silently did nothing. The
           * certificate designer had declared a sticky preview for ages and it
           * scrolled away like any other block, leaving a dead column.
           *
           * `overflow-x: clip` clips exactly the same way but creates NO scroll
           * container, so the Y axis stays `visible` and sticky works. Measured:
           * with `hidden` the preview sat 813px above the viewport after a
           * 1000px scroll; with `clip` it pins at its offset.
           *
           * `hidden` is kept as the fallback for browsers without `clip`
           * (Safari < 16): they keep today's behaviour — the layout is still
           * protected from wide tables, sticky simply stays inert there.
           */
          overflowX: "hidden",
          "@supports (overflow-x: clip)": { overflowX: "clip" },
        }}
      >
        <Navbar mode="dark" onToggleSidebar={() => setSidebarOpen((o) => !o)} />
        <Box sx={{ flex: 1, bgcolor: "background.default", minWidth: 0 }}>
          {children}
        </Box>
        <Footer />
      </Box>
    </Box>
  );
};

const DashboardSelector = () => {
  const user = useAuthStore((s) => s.user);
  
  switch (user?.role) {
    case "student":
      return <StudentDashboard />;
    case "teacher":
      return <TeacherDashboard />;
    case "university_admin":
      return <UniversityAdminDashboard />;
    case "head_of_faculty":
      return <HeadOfFacultyDashboard />;
    case "super_admin":
      return <SuperAdminDashboard />;
    default:
      return <Navigate to={ROUTES.LOGIN} />;
  }
};

const AppRoutes = () => {
  // On a university subdomain the root is that institution's front door.
  const { isTenant } = useTenant();
  // `initializing` is the one-time bootstrap session-check — NOT the same as
  // the store's `loading` (which also flips during login/register/etc and
  // would otherwise unmount the whole route tree, e.g. mid-registration).
  const initializing = useAuthStore((s) => s.initializing);
  const user = useAuthStore((s) => s.user);

  if (initializing) {
    return <AppLoader />;
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route
          path={ROUTES.LOGIN}
          element={
            <ProtectedRoute requireAuth={false}>
              <Login />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.REGISTER}
          element={
            <ProtectedRoute requireAuth={false}>
              <Register />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.FORGOT_PASSWORD}
          element={
            <ProtectedRoute requireAuth={false}>
              <ForgotPassword />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.RESET_PASSWORD}
          element={
            <ProtectedRoute requireAuth={false}>
              <ResetPassword />
            </ProtectedRoute>
          }
        />
        {/* Email verification — public, no auth required */}
        <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
        {/* Post-self-registration status — public, no session (pending approval) */}
        <Route path="/awaiting-approval" element={<AwaitingApproval />} />
        {/* Public certificate verification (no auth — for external verifiers) */}
        <Route path="/verify-certificate/:id" element={<CertificateVerify />} />

        {/* Billing — accessible post-registration before subscription is set */}
        <Route
          path="/select-plan"
          element={
            <ProtectedRoute allowedRoles={["university_admin"]}>
              <SelectPlanPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/billing/checkout"
          element={
            <ProtectedRoute allowedRoles={["university_admin"]}>
              <BillingCheckoutPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/billing"
          element={
            <ProtectedRoute allowedRoles={["university_admin"]}>
              <BillingDashboardPage />
            </ProtectedRoute>
          }
        />

        <Route
          path={ROUTES.DASHBOARD}
          element={
            <ProtectedRoute>
              <DashboardSelector />
            </ProtectedRoute>
          }
        />
        <Route
          path="/teaching"
          element={
            <ProtectedRoute allowedRoles={["teacher", "university_admin", "super_admin"]}>
              <TeachingCourses />
            </ProtectedRoute>
          }
        />
        <Route
          path="/enroll"
          element={
            <ProtectedRoute allowedRoles={["student"]}>
              <CourseCatalog />
            </ProtectedRoute>
          }
        />
        <Route
          path="/research"
          element={
            <ProtectedRoute allowedRoles={["student", "teacher", "university_admin"]}>
              <ResearchHub />
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-courses"
          element={
            <ProtectedRoute allowedRoles={["student"]}>
              <MyCourses />
            </ProtectedRoute>
          }
        />
        <Route
          path="/courses/:id"
          element={
            <ProtectedRoute>
              <CourseDetail />
            </ProtectedRoute>
          }
        />
        {/* Course content opens CourseDetail on its Curriculum tab. */}
        <Route
          path="/courses/:id/content"
          element={
            <ProtectedRoute>
              <CourseDetail />
            </ProtectedRoute>
          }
        />
        {/* Teacher block/lesson authoring studio. */}
        <Route
          path="/courses/:courseId/author"
          element={
            <ProtectedRoute allowedRoles={["teacher","university_admin","super_admin"]}>
              <CoursePageWrapper Component={CourseAuthoringStudio} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/courses/:courseId/lecture/:lectureId"
          element={
            <ProtectedRoute>
              <LecturePlayer />
            </ProtectedRoute>
          }
        />
        {/* Live broadcasting: list, teacher studio, viewer (live + recordings). */}
        <Route
          path="/courses/:courseId/live"
          element={
            <ProtectedRoute>
              <CourseBroadcasts />
            </ProtectedRoute>
          }
        />
        <Route
          path="/courses/:courseId/live/:broadcastId/studio"
          element={
            <ProtectedRoute allowedRoles={["teacher", "university_admin", "super_admin"]}>
              {/* Only a portal target — the studio itself is mounted by
                  <LiveSessionHost> above the router so it survives navigation. */}
              <StudioRoute />
            </ProtectedRoute>
          }
        />
        <Route
          path="/courses/:courseId/live/:broadcastId"
          element={
            <ProtectedRoute>
              <LiveViewer />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.CONTENT}
          element={
            <ProtectedRoute>
              <LecturePlayer />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.CREATE_COURSE}
          element={
            <ProtectedRoute allowedRoles={["teacher","university_admin","super_admin","head_of_faculty"]}>
              <CreateCourse />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.CREATE_CONTENT}
          element={
            <ProtectedRoute allowedRoles={["teacher","university_admin","super_admin"]}>
              <ContentUpload />
            </ProtectedRoute>
          }
        />
        <Route
          path="/quizzes"
          element={
            <ProtectedRoute>
              <QuizList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/quizzes/create"
          element={
            <ProtectedRoute allowedRoles={["teacher","university_admin","super_admin"]}>
              <QuizBuilder />
            </ProtectedRoute>
          }
        />
        <Route
          path="/quizzes/:quizId/edit"
          element={
            <ProtectedRoute allowedRoles={["teacher","university_admin","super_admin"]}>
              <QuizBuilder />
            </ProtectedRoute>
          }
        />
        <Route
          path="/quizzes/:quizId/submissions"
          element={
            <ProtectedRoute allowedRoles={["teacher","university_admin","super_admin"]}>
              <QuizSubmissions />
            </ProtectedRoute>
          }
        />
        <Route
          path="/quizzes/:quizId"
          element={
            <ProtectedRoute>
              <QuizScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/submissions/:submissionId/grade"
          element={
            <ProtectedRoute allowedRoles={["teacher","university_admin","super_admin"]}>
              <SubmissionGrader />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.DISCUSSIONS}
          element={
            <ProtectedRoute>
              <DiscussionBoard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/discussions/:id"
          element={
            <ProtectedRoute>
              <DiscussionThread />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.NOTIFICATIONS}
          element={
            <ProtectedRoute>
              <NotificationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/assignments"
          element={
            <ProtectedRoute>
              <AssignmentList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/assignments/create"
          element={
            <ProtectedRoute allowedRoles={["teacher","university_admin","super_admin"]}>
              <AssignmentCreate />
            </ProtectedRoute>
          }
        />
        <Route
          path="/assignments/:id/edit"
          element={
            <ProtectedRoute allowedRoles={["teacher","university_admin","super_admin"]}>
              <AssignmentCreate />
            </ProtectedRoute>
          }
        />
        <Route
          path="/assignments/:assignmentId/submissions"
          element={
            <ProtectedRoute allowedRoles={["teacher","university_admin","super_admin"]}>
              <AssignmentSubmissions />
            </ProtectedRoute>
          }
        />
        <Route
          path="/assignments/:assignmentId/grade/:submissionId"
          element={
            <ProtectedRoute allowedRoles={["teacher","university_admin","super_admin"]}>
              <AssignmentGrader />
            </ProtectedRoute>
          }
        />
        <Route
          path="/assignments/:assignmentId/submit"
          element={
            <ProtectedRoute allowedRoles={["student"]}>
              <AssignmentSubmissionPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.PROFILE}
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        {/* Enrollment management (University Admin + Super Admin) */}
        <Route
          path="/enrollment"
          element={
            <ProtectedRoute allowedRoles={["university_admin", "super_admin"]}>
              <EnrollmentPage />
            </ProtectedRoute>
          }
        />

        {/* University management (Super Admin) */}
        <Route
          path="/universities/create"
          element={
            <ProtectedRoute allowedRoles={["super_admin"]}>
              <CreateUniversity />
            </ProtectedRoute>
          }
        />
        <Route
          path="/universities/:id"
          element={
            <ProtectedRoute allowedRoles={["super_admin"]}>
              <ManageUniversity />
            </ProtectedRoute>
          }
        />
        {/* University admins edit their OWN institution (resolves to their
            universityId; no id in the URL). */}
        <Route
          path="/my-university"
          element={
            <ProtectedRoute allowedRoles={["university_admin", "super_admin"]}>
              <ManageUniversity />
            </ProtectedRoute>
          }
        />

        {/* Super Admin sidebar pages */}
        <Route
          path="/courses"
          element={
            <ProtectedRoute allowedRoles={["super_admin", "university_admin", "teacher", "head_of_faculty"]}>
              <CoursesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/users"
          element={
            <ProtectedRoute allowedRoles={["super_admin", "university_admin", "head_of_faculty"]}>
              <UsersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/departments"
          element={
            <ProtectedRoute allowedRoles={["super_admin", "university_admin", "head_of_faculty"]}>
              <DepartmentsPage />
            </ProtectedRoute>
          }
        />
        {/* A student reads their own record; staff read one of theirs by id.
            The backend refuses any id that is not the caller's own when the
            caller is a student, so this route is deliberately open to all. */}
        <Route
          path="/transcript"
          element={<ProtectedRoute><TranscriptPage /></ProtectedRoute>}
        />
        <Route
          path="/transcript/:studentId"
          element={
            <ProtectedRoute allowedRoles={["super_admin", "university_admin", "head_of_faculty", "teacher"]}>
              <TranscriptPage />
            </ProtectedRoute>
          }
        />
        {/* The institution's honour board. Open to every role on purpose:
            recognition only staff can see is not recognition. The server
            decides whether the caller also sees the averages. */}
        <Route
          path="/spotlight"
          element={<ProtectedRoute><SpotlightPage /></ProtectedRoute>}
        />
        {/* Closing a period writes permanent history — staff only, and the
            controller narrows a Dean to their own faculty on top of this. */}
        <Route
          path="/academic-periods"
          element={
            <ProtectedRoute allowedRoles={["super_admin", "university_admin", "head_of_faculty"]}>
              <AcademicPeriodsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tiers"
          element={
            <ProtectedRoute allowedRoles={["super_admin", "university_admin", "head_of_faculty"]}>
              <TiersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/analytics"
          element={
            <ProtectedRoute allowedRoles={["super_admin"]}>
              <AnalyticsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/universities"
          element={
            <ProtectedRoute allowedRoles={["super_admin"]}>
              <UniversitiesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/system-settings"
          element={
            <ProtectedRoute allowedRoles={["super_admin"]}>
              <SystemSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/error-monitoring"
          element={
            <ProtectedRoute allowedRoles={["super_admin"]}>
              <ErrorMonitoringPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/guide"
          element={
            <ProtectedRoute allowedRoles={["super_admin"]}>
              <SuperAdminGuidePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admins"
          element={
            <ProtectedRoute allowedRoles={["super_admin"]}>
              <AdminsPage />
            </ProtectedRoute>
          }
        />
        {/* Custom discussion emoji manager (institution admins) */}
        <Route
          path="/emoji-studio"
          element={
            <ProtectedRoute allowedRoles={["university_admin", "super_admin"]}>
              <EmojiStudioPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/calendar"
          element={
            <ProtectedRoute allowedRoles={["super_admin", "university_admin", "teacher", "student", "head_of_faculty"]}>
              <CalendarPage />
            </ProtectedRoute>
          }
        />

        <Route
          path={ROUTES.BOOKMARKS}
          element={
            <ProtectedRoute>
              <Bookmarks />
            </ProtectedRoute>
          }
        />

        <Route
          path="/grades"
          element={
            <ProtectedRoute allowedRoles={["student"]}>
              <GradesPage />
            </ProtectedRoute>
          }
        />
        {/* Certificate design. A Dean is admitted and narrowed server-side to
            their own departments; the institution-wide design is refused them. */}
        <Route
          path="/certificate-design"
          element={
            <ProtectedRoute allowedRoles={["university_admin", "super_admin", "head_of_faculty"]}>
              <CertificateDesigner />
            </ProtectedRoute>
          }
        />
        <Route
          path="/gradebook"
          element={
            <ProtectedRoute allowedRoles={["teacher", "university_admin", "super_admin", "head_of_faculty"]}>
              <Gradebook />
            </ProtectedRoute>
          }
        />
        {/* Student leaderboards — course/teacher scope for teachers, plus
            department/school scope for admins (rankingController enforces the
            actual per-scope authorization; this route just gets staff in). */}
        <Route
          path="/rankings"
          element={
            <ProtectedRoute allowedRoles={["teacher", "university_admin", "super_admin", "head_of_faculty"]}>
              <RankingsPage />
            </ProtectedRoute>
          }
        />
        {/* Attendance — teachers take the roll; admins also have access */}
        <Route
          path="/attendance"
          element={
            <ProtectedRoute allowedRoles={["teacher", "university_admin", "super_admin", "head_of_faculty"]}>
              <TakeAttendance />
            </ProtectedRoute>
          }
        />
        {/* Attendance reporting — class / teacher / department / university rollups */}
        <Route
          path="/attendance-reports"
          element={
            <ProtectedRoute allowedRoles={["university_admin", "super_admin", "head_of_faculty"]}>
              <AttendanceReports />
            </ProtectedRoute>
          }
        />
        {/* Faculty advising — a Dean OVERSEES advising rather than doing it, so
            they get a faculty-wide view instead of the mentor's own list. */}
        <Route
          path="/faculty-advising"
          element={
            <ProtectedRoute allowedRoles={["head_of_faculty", "university_admin", "super_admin"]}>
              <FacultyAdvising />
            </ProtectedRoute>
          }
        />
        {/* Advising — mentors (teachers / admins) manage their advisees' holds
            and the courses each advisee is allowed to enroll in. */}
        <Route
          path="/advisees"
          element={
            <ProtectedRoute allowedRoles={["teacher", "university_admin", "super_admin"]}>
              <AdviseesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/certificates"
          element={
            <ProtectedRoute allowedRoles={["student","teacher","university_admin","super_admin"]}>
              <MyCertificates />
            </ProtectedRoute>
          }
        />
        <Route
          path="/help"
          element={
            <ProtectedRoute>
              <HelpPage />
            </ProtectedRoute>
          }
        />

        {/* Learning Hub — student AI features */}
        <Route
          path="/learning-hub"
          element={
            <ProtectedRoute allowedRoles={["student"]}>
              <LearningHubPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/learning-hub/:courseId"
          element={
            <ProtectedRoute allowedRoles={["student"]}>
              <LearningHubPage />
            </ProtectedRoute>
          }
        />
        {/* Standalone adaptive / coach — student-only AI features (the backend
            keys insights off the logged-in user as an enrolled student, so they
            render empty/meaningless for teachers & admins). */}
        <Route
          path="/adaptive-learning/:courseId"
          element={
            <ProtectedRoute allowedRoles={["student"]}>
              <CoursePageWrapper Component={AdaptiveLearningDashboard} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/learning-coach/:courseId"
          element={
            <ProtectedRoute allowedRoles={["student"]}>
              <CoursePageWrapper Component={LearningCoachDashboard} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/course-analytics/:courseId"
          element={
            <ProtectedRoute allowedRoles={["teacher", "university_admin", "super_admin"]}>
              <CoursePageWrapper Component={LearningAnalyticsDashboard} />
            </ProtectedRoute>
          }
        />

        {/* Public resources pages */}
        <Route path="/docs" element={<Documentation />} />
        <Route path="/tutorials" element={<Tutorials />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/support" element={<Support />} />
        <Route path="/api" element={<Api />} />

        {/* Public product & marketing pages */}
        <Route path="/features" element={<Features />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/for-universities" element={<ForUniversities />} />
        <Route path="/for-teachers" element={<ForTeachers />} />
        <Route path="/for-students" element={<ForStudents />} />

        {/* Public company & legal pages — reachable by guests and signed-in users */}
        <Route path="/about" element={<AboutUs />} />
        <Route path="/careers" element={<Careers />} />
        <Route path="/press" element={<Press />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />

        {/* Public marketing homepage for signed-out visitors & crawlers;
            authenticated users go straight to their dashboard.
            On a TENANT subdomain there is no marketing site to show — the root
            is that institution's front door, so send signed-out visitors to
            their branded portal. Serving the Vitrine sales page at
            unikin.vitrinedev.site/ meant anyone given the bare subdomain (a
            demo link in an outreach email, say) landed on a pitch for Vitrine
            instead of their own login. */}
        <Route
          path="/"
          element={
            user ? (
              <Navigate to={ROUTES.DASHBOARD} replace />
            ) : isTenant ? (
              <Navigate to="/login" replace />
            ) : (
              <LandingPage />
            )
          }
        />
        {/* Access-denied target for ProtectedRoute (role mismatch). */}
        <Route path="/unauthorized" element={<Unauthorized />} />
        {/* Real 404 instead of silently bouncing every unknown URL to /dashboard. */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

const AppContent = () => {
  const initialize  = useAuthStore((s) => s.initialize);
  const initialized = useAuthStore((s) => s.initialized);
  const { university, isTenant, brandLoading } = useTenant();

  useEffect(() => {
    initialize();
  }, [initialize]);

  // Use the university's brand color when on a subdomain; fall back to Vitrine green.
  const primaryColor = university?.primaryColor || "#4CAF50";

  const theme = useMemo(() => {
    // A dark brand (deep navy, maroon, charcoal…) used as `primary` text/icons on
    // our dark-grey surfaces is nearly invisible. Lift it to the same hue at a
    // readable lightness so every `color="primary"` element across the tenant's
    // pages stays legible. Light/medium brands already pass and are unchanged.
    // contrastText is left to MUI so contained buttons keep a readable label.
    const accentColor = readableOn(primaryColor, "#242424", 4.5);
    return createTheme({
      palette: {
        mode: "dark",
        primary: { main: accentColor },
        secondary: { main: "#607D8B" },
        background: {
          default: "#1A1A1A",
          paper: "#242424",
        },
        text: {
          primary: "#FFFFFF",
          secondary: "#B0B0B0",
        },
      },
      components: {
        // Springy, tactile feedback across the whole app. Transforms only —
        // they never affect layout flow, so they're safe globally.
        MuiButton: {
          styleOverrides: {
            root: {
              transition:
                "transform 0.18s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.2s ease, background-color 0.2s ease",
              "&:active": { transform: "scale(0.96)" },
            },
            contained: {
              "&:hover": { transform: "translateY(-2px)" },
            },
          },
        },
        MuiIconButton: {
          styleOverrides: {
            root: {
              transition: "transform 0.18s cubic-bezier(0.34, 1.56, 0.64, 1)",
              "&:hover": { transform: "scale(1.12)" },
              "&:active": { transform: "scale(0.9)" },
            },
          },
        },
        MuiChip: {
          styleOverrides: {
            root: { transition: "transform 0.18s cubic-bezier(0.34, 1.56, 0.64, 1)" },
            clickable: { "&:hover": { transform: "scale(1.06)" } },
          },
        },
        MuiFab: {
          styleOverrides: {
            root: {
              transition: "transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)",
              "&:hover": { transform: "scale(1.08) translateY(-2px)" },
              "&:active": { transform: "scale(0.95)" },
            },
          },
        },
        // Determinate progress bars must read as empty at 0%. MUI's default
        // track is a translucent tint of the bar color (light-primary green in
        // this dark theme) that fills the whole bar and makes 0% look complete.
        // Force a neutral track app-wide; the fill (the `bar`) still uses the
        // `color` prop. Targets the color-variant classes too so it wins over
        // MUI's per-color track background.
        MuiLinearProgress: {
          styleOverrides: {
            root: { backgroundColor: "rgba(255, 255, 255, 0.13)" },
            colorPrimary: { backgroundColor: "rgba(255, 255, 255, 0.13)" },
            colorSecondary: { backgroundColor: "rgba(255, 255, 255, 0.13)" },
            bar: { borderRadius: "inherit" },
          },
        },
      },
    });
  },
  [primaryColor]);

  // Wait for BOTH the session bootstrap and (on a tenant) its branding before
  // the first paint. The theme is derived from university.primaryColor with a
  // Vitrine-green fallback, so rendering early paints the whole app green and
  // then swaps to the institution's colour once the fetch lands — a visible
  // flash of the wrong brand, ~120ms on a good connection and far longer on a
  // poor one, which is exactly where these tenants are.
  if (!initialized || (isTenant && brandLoading)) {
    return (
      <LoadingSpinner fullScreen text="Initializing application..." />
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <UpdateBanner />
      <Router>
        <UpgradeGateListener />
        {/* An in-progress broadcast lives HERE — outside <AppLayout> and, above
            all, outside the routes' <Suspense>. Inside that boundary, loading
            any lazy route chunk suspends the subtree and unmounts the studio,
            killing the camera and every peer connection mid-lecture. It gets its
            own boundary so route transitions can never take it down. */}
        <Suspense fallback={null}>
          <LiveSessionHost />
        </Suspense>
        {/* App-wide so the emoticon library — and the institution's own custom
            emoji — resolve anywhere a picker, reaction, or inline :code:
            renders, not just inside a discussion thread. */}
        <EmojiCatalogProvider>
          <AppLayout>
            <AppRoutes />
          </AppLayout>
        </EmojiCatalogProvider>
      </Router>
    </ThemeProvider>
  );
};

function App() {
  return (
    <TenantProvider>
      <AppContent />
    </TenantProvider>
  );
}

export default App;
